/**
 * recencyAtoms — recency / z-index hide state for the radial-magnetic
 * exploration model.
 *
 * Each node carries a `lastFocusedAt` timestamp. When new nodes are
 * placed via `ExpandNodeAtom`, we run a pairwise overlap pass between
 * the new arrivals and the existing canvas. Pairs with significant
 * overlap have their *older* member added to `HiddenNodesAtom`.
 *
 * On `pane click` (blur), `HiddenNodesAtom` is cleared and the overlap-
 * removal pass redistributes everything visibly.
 *
 * On `GoBackToFocusedAtom`, the restored focus's neighbours get a fresh
 * recency stamp so the previously-hidden subtree wins overlap pairs
 * again and re-emerges.
 */

import { atom, type PrimitiveAtom, type WritableAtom } from 'jotai';

import type { LayoutNode } from '../layout/IncrementalRadialLayout';
import { estimateLayoutNodeSize } from '../nodeSizing';

import { LayoutNodesAtom } from './layoutAtoms';

// ============================================================================
// State atoms
// ============================================================================

/** id → `lastFocusedAt` (Unix ms). Used as the priority key for hides. */
export const NodeRecencyAtom: PrimitiveAtom<Map<string, number>> & {
	init: Map<string, number>;
} = atom<Map<string, number>>(new Map());

/**
 * Set of node ids that are currently hidden because a more-recent
 * arrival overlapped them past `SIGNIFICANT_OVERLAP`. Cleared on blur.
 */
export const HiddenNodesAtom: PrimitiveAtom<Set<string>> & {
	init: Set<string>;
} = atom<Set<string>>(new Set<string>());

/**
 * id → `appearedAt` (Unix ms). Tracks nodes that joined the graph
 * recently so the node renderer can fade them in from opacity 0 → 1.
 * Entries older than `APPEAR_TTL_MS` are considered settled.
 *
 * The position of an appearing node is *not* animated — only opacity.
 * The magnetic pull handles position interpolation for already-visible
 * siblings, while fresh arrivals land at their final rest positions
 * and just fade in visually.
 */
export const AppearingNodesAtom: PrimitiveAtom<Map<string, number>> & {
	init: Map<string, number>;
} = atom<Map<string, number>>(new Map());

/** How long (ms) a new node stays in the appearing set before settling. */
export const APPEAR_TTL_MS = 400;

// ============================================================================
// Tunables
// ============================================================================

/** AABB area-overlap fraction (relative to smaller box) that triggers hide. */
const SIGNIFICANT_OVERLAP = 0.5;

/**
 * Per-typename size estimates used by the overlap pass before ReactFlow
 * has measured the DOM. Mirrors `IncrementalRadialLayout::estimateSize`
 * but the numbers don't need to be perfectly identical — a slight
 * over-estimate is fine, it just biases toward more hides.
 */
function estimateNodeSize(node: LayoutNode): { width: number; height: number } {
	return estimateLayoutNodeSize(node.sizingInput);
}

function aabbOverlapFraction(
	a: { x: number; y: number },
	sa: { width: number; height: number },
	b: { x: number; y: number },
	sb: { width: number; height: number },
): number {
	const overlapW = sa.width / 2 + sb.width / 2 - Math.abs(a.x - b.x);
	const overlapH = sa.height / 2 + sb.height / 2 - Math.abs(a.y - b.y);
	if (overlapW <= 0 || overlapH <= 0) {
		return 0;
	}
	const overlapArea = overlapW * overlapH;
	const smallerArea = Math.min(sa.width * sa.height, sb.width * sb.height);
	if (smallerArea <= 0) {
		return 0;
	}
	return Math.min(1, overlapArea / smallerArea);
}

// ============================================================================
// Write atoms
// ============================================================================

/**
 * Stamp `lastFocusedAt = Date.now()` for every supplied node id.
 * Idempotent — calling with the same id twice in the same tick just
 * keeps the latest timestamp.
 */
export const StampRecencyAtom: WritableAtom<null, [ids: readonly string[]], void> & {
	init: null;
} = atom(null, (get, set, ids: readonly string[]) => {
	if (ids.length === 0) {
		return;
	}
	const now = Date.now();
	const next = new Map(get(NodeRecencyAtom));
	for (const id of ids) {
		next.set(id, now);
	}
	set(NodeRecencyAtom, next);
});

/**
 * Run a pairwise overlap pass between `newIds` and every other layout
 * node, hiding the older member of each significantly-overlapping pair.
 *
 * Older = lower `lastFocusedAt`. Nodes without a recency entry are
 * treated as `0` so they're always older than any stamped node.
 */
export const EvaluateOverlapHidesAtom: WritableAtom<null, [newIds: readonly string[]], void> & {
	init: null;
} = atom(null, (get, set, newIds: readonly string[]) => {
	if (newIds.length === 0) {
		return;
	}
	const layout = get(LayoutNodesAtom);
	const recency = get(NodeRecencyAtom);
	const hidden = new Set(get(HiddenNodesAtom));

	const newSet = new Set(newIds);
	const newNodes: LayoutNode[] = [];
	const otherNodes: LayoutNode[] = [];
	for (const [id, node] of layout) {
		if (hidden.has(id)) {
			continue;
		} // ignore already-hidden nodes
		if (newSet.has(id)) {
			newNodes.push(node);
		} else {
			otherNodes.push(node);
		}
	}

	const sizeOf = (n: LayoutNode) => estimateNodeSize(n);
	const recencyOf = (id: string) => recency.get(id) ?? 0;

	let changed = false;
	for (const a of newNodes) {
		const sa = sizeOf(a);
		for (const b of otherNodes) {
			const sb = sizeOf(b);
			if (aabbOverlapFraction(a, sa, b, sb) >= SIGNIFICANT_OVERLAP) {
				const olderId = recencyOf(a.id) < recencyOf(b.id) ? a.id : b.id;
				if (!hidden.has(olderId)) {
					hidden.add(olderId);
					changed = true;
				}
			}
		}
	}

	if (changed) {
		set(HiddenNodesAtom, hidden);
	}
});

/** Reset the hide set (used on blur). */
export const ClearHiddenNodesAtom: WritableAtom<null, [], void> & {
	init: null;
} = atom(null, (_get, set) => {
	set(HiddenNodesAtom, new Set<string>());
});

/**
 * Stamp `appearedAt = Date.now()` for every supplied id. Called by
 * `InitRadialLayoutAtom` (first paint) and `ExpandNodeAtom` (every
 * subsequent expansion) for `delta.added` — i.e. only for nodes that
 * are joining the graph for the first time.
 */
export const MarkAppearingAtom: WritableAtom<null, [ids: readonly string[]], void> & {
	init: null;
} = atom(null, (get, set, ids: readonly string[]) => {
	if (ids.length === 0) {
		return;
	}
	const now = Date.now();
	const next = new Map(get(AppearingNodesAtom));
	for (const id of ids) {
		next.set(id, now);
	}
	set(AppearingNodesAtom, next);
});

/**
 * Evict entries whose `appearedAt` is older than `APPEAR_TTL_MS`. Keeps
 * the atom from growing unbounded and ensures `isAppearing` settles to
 * `false` for every node after its fade-in completes.
 */
export const PruneAppearingAtom: WritableAtom<null, [], void> & {
	init: null;
} = atom(null, (get, set) => {
	const current = get(AppearingNodesAtom);
	if (current.size === 0) {
		return;
	}
	const cutoff = Date.now() - APPEAR_TTL_MS;
	let anyStale = false;
	for (const ts of current.values()) {
		if (ts <= cutoff) {
			anyStale = true;
			break;
		}
	}
	if (!anyStale) {
		return;
	}
	const next = new Map<string, number>();
	for (const [id, ts] of current) {
		if (ts > cutoff) {
			next.set(id, ts);
		}
	}
	set(AppearingNodesAtom, next);
});
